import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '../primitives/Button'
import { useHitTarget } from '../responsive/hitTarget'
import './iconCatalogue.css'

/**
 * How far the edge fade reaches, in px. Exported because two things must
 * agree on it: `iconCatalogue.css` sizes the mask with it, and the reveal below
 * keeps the active tab this far clear of an edge so it is never under the
 * fade. `IconGroupTabs.test.tsx` reads the stylesheet and holds the two
 * together.
 */
export const TAB_FADE_PX = 24

/** Sub-pixel scroll positions: a zoomed page stops a pixel short of the end. */
const SLACK = 1

type Group = { key: string | null; label: string }

/**
 * The icon picker's group tabs — #455, the user's decision of 2026-09-22.
 *
 * 53 groups plus "All" in ONE row that scrolls horizontally inside the pane.
 * It never wraps (eight rows of chips above the icons they select) and never
 * widens the dialog. What was missing was any sign that the row continued: it
 * was clipped at the pane's edge on "Transport" or "Groceries", and nothing
 * said 54 existed. Three affordances now say so:
 *
 * - **Edge fades**, only on a side with more content. A MASK on the scroller
 *   (`iconCatalogue.css`), not a gradient painted over it: the pane is the
 *   dialog's glass, so any fill colour would composite differently over each
 *   backdrop and each theme, while a mask only fades the tabs themselves.
 * - **Previous / Next buttons** that scroll by about one visible width.
 *   `aria-disabled` at the ends rather than `disabled` or hidden: a disabled
 *   button drops focus the instant the user's own keypress reaches the end,
 *   and a hidden one shifts the row under the pointer. So the guard lives in
 *   the click handler, and the test presses Next at the end to prove it.
 * - **The active tab is kept in view** whenever it changes — and on mount,
 *   which is how the row returns after a search (search replaces the tabs, so
 *   clearing it mounts a fresh row at scroll 0 on the group that was active).
 *
 * Scrolling is instant, not smooth: a smooth scroll is motion, and M0-R20
 * lists every motion this app has. Touch and trackpad scrolling are the
 * browser's own and untouched; scroll-snap aligns a tab to the start edge.
 */
export function IconGroupTabs({
  groups,
  value,
  onChange,
  label = 'Icon group',
  allLabel = 'All',
}: {
  groups: Group[]
  value: string | null
  onChange: (key: string | null) => void
  /** The radiogroup's accessible name. */
  label?: string
  /** The leading "everything" tab; `null` leaves it out (the category picker has no such view). */
  allLabel?: string | null
}) {
  // 33px, or 44px below 640px (M0-R29) — the tabs' own height, and the
  // buttons' height AND width, so the arrows are square targets.
  const chip = useHitTarget('form')
  // Scopes the tabs' native radio exclusivity to this instance.
  const name = useId()
  const viewport = useRef<HTMLDivElement>(null)
  const [moreStart, setMoreStart] = useState(false)
  const [moreEnd, setMoreEnd] = useState(false)

  const measure = useCallback(() => {
    const el = viewport.current
    if (el === null) return
    setMoreStart(el.scrollLeft > SLACK)
    setMoreEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - SLACK)
  }, [])

  // The row's width changes with the dialog (the pane opening, a rotation), so
  // what is "more" is re-measured whenever the scroller resizes, not only on
  // scroll. jsdom has no ResizeObserver; there the mount measure is all.
  useEffect(() => {
    const el = viewport.current
    measure()
    if (el === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [measure])

  // Before paint, so the row never flashes at the wrong offset. Measured from
  // rects rather than `scrollIntoView`, which would also scroll the dialog
  // panel vertically, and which keeps no clearance for the fade.
  useLayoutEffect(() => {
    const el = viewport.current
    const tab = el?.querySelector('input:checked')?.closest('.icon-group-tabs__tab')
    if (el == null || tab == null) return
    const view = el.getBoundingClientRect()
    const box = tab.getBoundingClientRect()
    const max = Math.max(0, el.scrollWidth - el.clientWidth)
    let to = el.scrollLeft
    if (box.left < view.left + TAB_FADE_PX) to -= view.left + TAB_FADE_PX - box.left
    else if (box.right > view.right - TAB_FADE_PX) to += box.right - (view.right - TAB_FADE_PX)
    el.scrollLeft = Math.min(max, Math.max(0, to))
    measure()
  }, [value, measure])

  function step(direction: -1 | 1) {
    const el = viewport.current
    // The ends are guarded here, not by `disabled` — see the docblock.
    if (el === null || (direction < 0 ? !moreStart : !moreEnd)) return
    // One width less a fade at each side, so the tab that was half-hidden
    // under the far fade is fully shown after the step instead of skipped.
    const by = Math.max(TAB_FADE_PX, el.clientWidth - 2 * TAB_FADE_PX)
    // The browser clamps to the ends; the scroll event it fires re-measures.
    el.scrollLeft = el.scrollLeft + direction * by
    measure()
  }

  return (
    <div className="icon-group-tabs">
      <Button
        type="button"
        variant="chip"
        size="form"
        className="icon-group-tabs__step"
        style={{ minWidth: chip }}
        aria-label="Previous groups"
        aria-disabled={!moreStart}
        onClick={() => step(-1)}
      >
        <span aria-hidden="true">‹</span>
      </Button>
      <div
        ref={viewport}
        className="icon-group-tabs__viewport"
        role="radiogroup"
        aria-label={label}
        data-more-start={moreStart ? '' : undefined}
        data-more-end={moreEnd ? '' : undefined}
        onScroll={measure}
      >
        {(allLabel === null ? groups : [{ key: null, label: allLabel }, ...groups]).map((g) => (
          <label
            key={g.key ?? 'all'}
            className="icon-group-tabs__tab"
            style={{ minHeight: chip }}
          >
            <input
              className="icon-catalogue__input"
              type="radio"
              name={name}
              checked={value === g.key}
              onChange={() => onChange(g.key)}
            />
            {g.label}
          </label>
        ))}
      </div>
      <Button
        type="button"
        variant="chip"
        size="form"
        className="icon-group-tabs__step"
        style={{ minWidth: chip }}
        aria-label="Next groups"
        aria-disabled={!moreEnd}
        onClick={() => step(1)}
      >
        <span aria-hidden="true">›</span>
      </Button>
    </div>
  )
}
